import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/auth-context';
import { fetchUserProfileById, updateUserProfileAction } from '../apiEndpoints';
import { Input } from '@heroui/input';
import { GoogleMap, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import Avatar from './Avatar';
import ActiveSessionsSection from './ActiveSessionsSection';
import { bumpAvatarCacheBust } from '@/utils/avatarCacheBust';
import { IconChevronRight as ChevronRight, IconLogout as LogOut, IconTrash as Trash2 } from '@tabler/icons-react';
import { cardTitle, label as fieldLabel, surface } from '@/components/ui/design';

const flatInputClassNames = {
    inputWrapper: '!bg-white dark:!bg-white/[0.06] h-12 rounded-2xl shadow-none border-none ring-1 ring-black/[0.06] dark:ring-white/10 data-[hover=true]:!bg-white dark:data-[hover=true]:!bg-white/[0.08] group-data-[focus=true]:!bg-white dark:group-data-[focus=true]:!bg-white/[0.08] group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:group-data-[focus=true]:ring-nightAccent/60',
    input: '!text-grey10 dark:!text-white text-base placeholder:!text-grey60 dark:placeholder:!text-nightMuted',
};

// Static libraries array to prevent performance warnings
const libraries: ("places")[] = ["places"];

export default function AccountTab() {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { user: authUser, logout } = useAuth();
    const userId = authUser?.userId;

    // Fetch user profile from backend
    const { data: userProfile, isLoading, isError } = useQuery({
        queryKey: ['userProfile', userId],
        queryFn: () => {
            if (!userId) return Promise.reject('No user ID');
            return fetchUserProfileById(userId);
        },
        enabled: !!userId,
    });

    // Local state for form fields
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [location, setLocation] = useState('');
    const [addressObject, setAddressObject] = useState({
        fullAddress: '',
        city: '',
        country: '',
        state: '',
        zipcode: '',
        latitude: '34.0522',
        longitude: '-118.2437'
    });
    const [avatar, setAvatar] = useState<string | null>(null);
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [avatarUploadLoading, setAvatarUploadLoading] = useState(false);
    const [avatarRemoveLoading, setAvatarRemoveLoading] = useState(false);

    // Map and location state
    const [lat, setLat] = useState(34.0522); // Default: Los Angeles
    const [lng, setLng] = useState(-118.2437);
    const mapCenter = React.useMemo(() => ({ lat, lng }), [lat, lng]);
    const [autocomplete, setAutocomplete] = useState<any>(null);
    const [map, setMap] = useState<any>(null);
    const [addressLoading, setAddressLoading] = useState(false);
    const [lastFetchedCoords, setLastFetchedCoords] = useState<{ lat: number; lng: number } | null>(null);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    const mapContainerStyle = {
        width: '100%',
        height: '300px',
        borderRadius: '16px',
        minHeight: '300px',
        maxHeight: '300px'
    };
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyBKbmD1FAPDpYvIFHjUJpeZ9XbohnH_QFI',
        libraries: libraries,
    });

    // Helper function to extract complete address object from Google address components
    function extractAddressFromComponents(addressComponents: any[]): { country: string; state: string; city: string; zipcode: string; fullAddress: string } {
        if (!addressComponents || !Array.isArray(addressComponents)) {
            return { country: '', state: '', city: '', zipcode: '', fullAddress: '' };
        }

        const address: { country: string; state: string; city: string; zipcode: string; fullAddress: string } = {
            country: '',
            state: '',
            city: '',
            zipcode: '',
            fullAddress: ''
        };

        // Extract country
        const country = addressComponents.find(component =>
            component.types.includes('country')
        );
        if (country) address.country = country.long_name;

        // Extract state/province
        const state = addressComponents.find(component =>
            component.types.includes('administrative_area_level_1')
        );
        if (state) address.state = state.long_name;

        // Extract city
        const city = addressComponents.find(component =>
            component.types.includes('locality')
        );
        if (city) address.city = city.long_name;

        // Extract postal code
        const zipcode = addressComponents.find(component =>
            component.types.includes('postal_code')
        );
        if (zipcode) address.zipcode = zipcode.long_name;

        return address;
    }

    function onLoadAutocomplete(ac: any) {
        setAutocomplete(ac);
    }

    function onPlaceChanged() {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setLat(newLat);
                setLng(newLng);

                // Extract complete address from address components
                const addressData = extractAddressFromComponents(place.address_components);

                setLocation(place.formatted_address || '');
                setAddressObject({
                    fullAddress: place.formatted_address || '',
                    city: addressData.city,
                    country: addressData.country,
                    state: addressData.state,
                    zipcode: addressData.zipcode,
                    latitude: newLat.toString(),
                    longitude: newLng.toString()
                });

                if (map) {
                    map.panTo({ lat: newLat, lng: newLng });
                }

                updateProfile.mutate({
                    name,
                    email,
                    location: place.formatted_address || '',
                    addressObject: {
                        fullAddress: place.formatted_address || '',
                        city: addressData.city,
                        country: addressData.country,
                        state: addressData.state,
                        zipcode: addressData.zipcode,
                        latitude: newLat.toString(),
                        longitude: newLng.toString()
                    },
                    avatarFile
                });
            }
        }
    }

    function onMapLoad(mapInstance: any) {
        setMap(mapInstance);
    }

    async function fetchAddressFromCoords(lat: number, lng: number) {
        setAddressLoading(true);
        fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBTl2hQB_YKIYPeWLD2qJ0G08yTo7SErP8`
        )
            .then(response => response.json())
            .then(data => {
                if (data.results && data.results[0]) {
                    const result = data.results[0];
                    // Extract complete address from address components
                    const addressData = extractAddressFromComponents(result.address_components);

                    setLocation(result.formatted_address);
                    setAddressObject({
                        fullAddress: result.formatted_address,
                        city: addressData.city,
                        country: addressData.country,
                        state: addressData.state,
                        zipcode: addressData.zipcode,
                        latitude: lat.toString(),
                        longitude: lng.toString()
                    });
                }
            })
            .catch(() => {
                // Optionally handle error
            })
            .finally(() => {
                setAddressLoading(false);
            });
    }

    // Replace onIdle handler to debounce and only fetch if center changed
    function handleMapIdle() {
        if (map) {
            const center = map.getCenter();
            const newLat = center.lat();
            const newLng = center.lng();
            // Only fetch if coordinates changed significantly (0.00001 deg threshold)
            if (!lastFetchedCoords || Math.abs(newLat - lastFetchedCoords.lat) > 0.00001 || Math.abs(newLng - lastFetchedCoords.lng) > 0.00001) {
                setLastFetchedCoords({ lat: newLat, lng: newLng });
                // Debounce fetch
                if (debounceTimer) clearTimeout(debounceTimer);
                const timer = setTimeout(() => {
                    fetchAddressFromCoords(newLat, newLng);
                }, 400); // 400ms debounce
                setDebounceTimer(timer);
            }
            setLat(newLat);
            setLng(newLng);
            setAddressObject(prev => ({ ...prev, latitude: newLat.toString(), longitude: newLng.toString() }));
        }
    }

    // Handle window resize to prevent map layout shifts
    React.useEffect(() => {
        const handleResize = () => {
            if (map) {
                // Trigger map resize after a short delay to prevent flicker
                setTimeout(() => {
                    if (map) {
                        const center = map.getCenter();
                        if (center) {
                            map.panTo(center);
                        }
                    }
                }, 100);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [map]);

    // Populate form fields when user data loads
    React.useEffect(() => {
        if (userProfile) {
            setName(userProfile.fullName || '');
            setEmail(userProfile.email || '');

            // Parse address object if it exists, otherwise use the address string
            if (typeof userProfile.address === 'object' && userProfile.address) {
                const fullAddress = userProfile.address.fullAddress || userProfile.address.line1 || '';
                setLocation(fullAddress);
                setAddressObject({
                    fullAddress: fullAddress,
                    city: userProfile.address.city || '',
                    country: userProfile.address.country || userProfile.address.countryCode || '',
                    state: userProfile.address.state || userProfile.address.stateProvinceCode || '',
                    zipcode: userProfile.address.zipcode || userProfile.address.zipCodePostalCode || '',
                    latitude: userProfile.address.latitude || lat.toString(),
                    longitude: userProfile.address.longitude || lng.toString()
                });

                // Set map coordinates if user has location data
                if (userProfile.address.latitude && userProfile.address.longitude) {
                    setLat(parseFloat(userProfile.address.latitude));
                    setLng(parseFloat(userProfile.address.longitude));
                }
            } else {
                const addressString = userProfile.address || '';
                setLocation(addressString);
                setAddressObject({
                    fullAddress: addressString,
                    city: '',
                    country: '',
                    state: '',
                    zipcode: '',
                    latitude: lat.toString(),
                    longitude: lng.toString()
                });
            }

            setAvatar(userProfile.profileImage || null);

            // Set map coordinates if user has location data (fallback to old format)
            if ((userProfile as any).lat && (userProfile as any).lng) {
                setLat((userProfile as any).lat);
                setLng((userProfile as any).lng);
            }
        }
    }, [userProfile]);

    // Update profile using backend
    const updateProfile = useMutation({
        mutationFn: async (fields: { name: string; email: string; location: string; addressObject: any; avatarFile?: File | null }) => {
            if (!userId) throw new Error('No user ID');
            const formData = new FormData();
            formData.append('firstName', fields.name);
            //formData.append('email', fields.email);
            formData.append('address', JSON.stringify(fields.addressObject));
            if (fields.avatarFile) formData.append('file', fields.avatarFile);
            return updateUserProfileAction(userId, formData);
        },
        onSuccess: () => {
            setProfileSuccess(true);
            setTimeout(() => setProfileSuccess(false), 2000);
            bumpAvatarCacheBust();
            queryClient.invalidateQueries({ queryKey: ['userProfile', userId] });
            queryClient.invalidateQueries({ queryKey: ['authUser'] });
            queryClient.invalidateQueries({ queryKey: ['user', userId] });
        },
        onError: (err: any) => {
            setProfileError(err?.message || 'Failed to update profile');
        },
    });

    function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarUploadLoading(true);
            setAvatarFile(file);
            setAvatar(URL.createObjectURL(file));
            setAvatarUploadLoading(false);
        }
    }

    function handleProfileSave() {
        setProfileError('');
        updateProfile.mutate({ name, email, location, addressObject, avatarFile });
    }

    function handleNameBlur() {
        if (userProfile && name !== (userProfile.fullName || '')) {
            handleProfileSave();
        }
    }

    function handleDeleteAccount() {
        if (window.confirm('Are you sure you want to permanently delete your account? This cannot be undone.')) {
            // Deleting an account requires backend support that does not exist yet.
        }
    }

    function handleRemoveAvatar() {
        setProfileError('');
        setAvatarRemoveLoading(true);

        // Optimistically update UI
        setAvatar(null);
        setAvatarFile(null);

        if (!userId) {
            setProfileError('User not authenticated');
            setAvatarRemoveLoading(false);
            return;
        }

        deleteUserProfileImage()
            .then(() => {
                setProfileSuccess(true);
                setTimeout(() => setProfileSuccess(false), 2000);
                bumpAvatarCacheBust();
                // Invalidate queries to refresh data
                queryClient.invalidateQueries({ queryKey: ['userProfile', userId] });
                queryClient.invalidateQueries({ queryKey: ['authUser'] });
                queryClient.invalidateQueries({ queryKey: ['user', userId] });
                setAvatarRemoveLoading(false);
            })
            .catch((err) => {
                // Revert optimistic update on error
                if (userProfile?.profileImage) {
                    setAvatar(userProfile.profileImage);
                }
                setProfileError(err?.message || 'Failed to remove avatar. Please try again.');
                setAvatarRemoveLoading(false);
            });
    }

    function handlePasswordSave(e: React.FormEvent) {
        e.preventDefault();
        setPasswordError('');
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }
        // Keep password change logic as is (simulate or connect to backend if available)
    }

    if (isLoading) {
        return (
            <div className="w-full flex justify-center">
                <div className="w-full sm:max-w-full flex flex-col gap-6 sm:gap-8">
                    <div className="animate-pulse space-y-6 sm:space-y-8">
                        {/* Header skeleton */}
                        <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4 sm:gap-0">
                            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:w-auto">
                                <div className="w-20 h-20 sm:w-16 sm:h-16 bg-grey99 dark:bg-white/10 rounded-full"></div>
                                <div className="flex gap-2 justify-center sm:justify-start w-full sm:w-auto">
                                    <div className="h-10 bg-grey99 dark:bg-white/10 rounded-full w-24"></div>
                                    <div className="h-10 bg-grey99 dark:bg-white/10 rounded-full w-24"></div>
                                </div>
                            </div>
                            <div className="w-full sm:w-auto">
                                <div className="h-10 bg-grey99 dark:bg-white/10 rounded-full w-full sm:w-32"></div>
                            </div>
                        </div>

                        {/* Form fields skeleton */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div className="space-y-2">
                                <div className="h-4 bg-grey99 dark:bg-white/10 rounded-full w-16"></div>
                                <div className="h-12 bg-grey99 dark:bg-white/10 rounded-2xl"></div>
                            </div>
                            <div className="space-y-2">
                                <div className="h-4 bg-grey99 dark:bg-white/10 rounded-full w-20"></div>
                                <div className="h-12 bg-grey99 dark:bg-white/10 rounded-2xl"></div>
                            </div>
                        </div>

                        {/* Location and map skeleton */}
                        <div className="space-y-2">
                            <div className="h-4 bg-grey99 dark:bg-white/10 rounded-full w-48"></div>
                            <div className="h-12 bg-grey99 dark:bg-white/10 rounded-2xl mb-2"></div>
                            <div className="w-full h-[300px] bg-grey99 dark:bg-white/10 rounded-[28px]"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (isError) return <div className="w-full flex justify-center items-center rounded-[28px] bg-red-50 py-12 text-red-600 dark:bg-red-500/10 dark:text-red-400">Failed to load profile.</div>;

    return (
        <div className="w-full flex flex-col gap-6 sm:gap-8">
            {profileError && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:bg-red-500/10 dark:text-red-400">{profileError}</div>}
            {profileSuccess && <div className="rounded-2xl bg-primary99 px-4 py-3 text-sm font-medium text-primary20 dark:bg-white/[0.06] dark:text-nightAccent">Profile updated!</div>}

            {/* Avatar + Name */}
            <div className="flex items-center gap-4 sm:gap-5">
                <button
                    type="button"
                    className="relative flex-shrink-0 rounded-full ring-4 ring-grey99 transition-shadow hover:ring-primary/30 dark:ring-white/10 dark:hover:ring-nightAccent/40"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Change profile photo"
                    disabled={avatarUploadLoading}
                >
                    <Avatar src={avatar} name={name} seed={userId} size={64} className="w-14 h-14 sm:w-16 sm:h-16" />
                </button>
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(e) => {
                        handleAvatarUpload(e);
                        handleProfileSave();
                    }}
                />
                <h1 className="min-w-0 flex-1 truncate pb-[0.06em] text-[clamp(26px,4vw,48px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">{name || 'My Account'}</h1>
                <button
                    type="button"
                    onClick={handleProfileSave}
                    disabled={updateProfile.isPending}
                    className="inline-flex h-11 flex-shrink-0 items-center justify-center rounded-full bg-grey10 px-6 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60 dark:bg-white dark:text-nightBg dark:hover:bg-grey90"
                >
                    {updateProfile.isPending ? 'Saving...' : 'Save'}
                </button>
            </div>

            {/* My Information */}
            <div className={`${surface} flex flex-col gap-5 p-5 sm:p-8`}>
                <h2 className={cardTitle}>My Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className={fieldLabel}>Name</label>
                        <Input
                            aria-label="Name"
                            className="no-global-input"
                            placeholder="Name..."
                            value={name}
                            onChange={e => setName(e.target.value)}
                            onBlur={handleNameBlur}
                            classNames={flatInputClassNames}
                        />
                    </div>
                    <div>
                        <label className={fieldLabel}>Email</label>
                        <Input
                            aria-label="Email"
                            className="no-global-input"
                            value={email}
                            isDisabled
                            onChange={e => setEmail(e.target.value)}
                            classNames={flatInputClassNames}
                        />
                    </div>
                </div>
            </div>

            {/* Location */}
            <div className={`${surface} flex flex-col gap-5 p-5 sm:p-8`}>
                <h2 className={cardTitle}>Location</h2>
                <div>
                    <label className={fieldLabel}>Location</label>
                    {!isLoaded && (
                        <div className="w-full h-[300px] bg-white dark:bg-white/[0.06] rounded-[20px] flex items-center justify-center">
                            <div className="text-grey40 dark:text-nightMuted">Loading map...</div>
                        </div>
                    )}
                    {isLoaded && (
                        <div className="flex flex-col gap-3">
                            <Autocomplete
                                onLoad={onLoadAutocomplete}
                                onPlaceChanged={onPlaceChanged}
                                options={{
                                    componentRestrictions: { country: 'us' },
                                    types: ['establishment', 'geocode']
                                }}
                            >
                                <Input
                                    aria-label="Location"
                                    className="w-full no-global-input"
                                    placeholder="Search for a place or address in the US..."
                                    value={location}
                                    onChange={e => {
                                        setLocation(e.target.value);
                                        setAddressObject(prev => ({ ...prev, fullAddress: e.target.value }));
                                    }}
                                    classNames={flatInputClassNames}
                                />
                            </Autocomplete>
                            <div className="rounded-[20px] ring-1 ring-black/[0.06] dark:ring-white/10" style={{
                                position: 'relative',
                                width: '100%',
                                height: '300px',
                                minHeight: '300px',
                                maxHeight: '300px',
                                overflow: 'hidden',
                                overscrollBehavior: 'none'
                            }}>
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    zoom={14}
                                    onLoad={onMapLoad}
                                    onIdle={handleMapIdle}
                                    center={mapCenter}
                                    options={{
                                        disableDefaultUI: false,
                                        zoomControl: true,
                                        mapTypeControl: false,
                                        scaleControl: false,
                                        streetViewControl: false,
                                        rotateControl: false,
                                        fullscreenControl: false,
                                        gestureHandling: 'cooperative'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -100%)',
                                    pointerEvents: 'none',
                                    zIndex: 10
                                }}>
                                    <img src="/marker.png" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={`${surface} p-5`}><ActiveSessionsSection /></div>

            {/* Account actions */}
            <div className={`${surface} flex flex-col gap-1 p-2 mb-4`}>
                <button
                    type="button"
                    onClick={() => logout()}
                    className="group flex w-full items-center gap-3 rounded-[20px] px-4 py-4 text-left text-base font-semibold text-[#E53935] transition-colors hover:bg-white dark:hover:bg-white/[0.06]"
                >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E53935]/10">
                        <LogOut className="w-5 h-5 rotate-180" />
                    </span>
                    <span className="flex-1">Logout</span>
                    <ChevronRight className="h-5 w-5 text-grey70 transition-transform group-hover:translate-x-0.5 dark:text-white/30" />
                </button>
                <button
                    type="button"
                    onClick={handleDeleteAccount}
                    className="group flex w-full items-center gap-3 rounded-[20px] px-4 py-4 text-left text-base font-semibold text-[#E53935] transition-colors hover:bg-white dark:hover:bg-white/[0.06]"
                >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E53935]/10">
                        <Trash2 className="w-5 h-5" />
                    </span>
                    <span className="flex-1">Delete Account</span>
                    <ChevronRight className="h-5 w-5 text-grey70 transition-transform group-hover:translate-x-0.5 dark:text-white/30" />
                </button>
            </div>
        </div>
    );
}
